/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/core/IncreaseVersion.ts
 * @create:      2025-07-01 13:21:53.261
 * @modify:      2025-07-11 21:19:12.930
 * @version:     6.0.0
 * @times:       5
 * @lines:       116
 * @copyright:   Copyright © 2018-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

/**
 * Increments the given semantic version string by increasing the patch version.
 *
 * @param version - The semantic version string (e.g., "1.2.3") to increment.
 * @returns The incremented semantic version string.
 *
 * @remarks
 * This function assumes the input is a valid semantic version in the format "major.minor.patch".
 * 
 * @since 3.0.3
 * @version 2021-12-06
 */
export function IncreaseVersion(version: string): string;
/**
 * Increases a specific segment of a semantic version string by a given increment.
 *
 * @param version - The version string in semantic versioning format (e.g., "1.2.3").
 * @param index - The zero-based index of the version segment to increment (0 for major, 1 for minor, 2 for patch).
 * @param increment - The amount to increase the specified segment by.
 * @returns The updated version string with the specified segment incremented.
 * 
 * @since 3.0.3
 * @version 2021-12-06
 */
export function IncreaseVersion(version: string, index: number, increment: number): string;
export function IncreaseVersion(version: string, index?: number, increment?: number): string {
    const array = version.match(/\d+/g);
    if (!array) {
        return version;
    }

    let numbers = array.map(Number);
    const strings = version.match(/[^\d]+/g);

    if (index === undefined) {
        index = -1;
    }

    if (increment === undefined) {
        increment = 1;
    }

    if (index < 0) {
        const temp = numbers.reverse();
        temp[-index - 1] += increment;
        numbers = temp.reverse();
    } else {
        numbers[index - 1] += increment;
    }

    if (numbers && strings) {
        const startsWithString = version.startsWith(strings[0]);
        const result = [];

        let s, n;
        do {
            s = strings.shift();
            n = numbers.shift();

            if (startsWithString) {
                if (s !== undefined) {
                    result.push(s);
                }

                if (n !== undefined) {
                    result.push(n);
                }
            } else {
                if (n !== undefined) {
                    result.push(n);
                }

                if (s !== undefined) {
                    result.push(s);
                }
            }
        } while (s !== undefined || n !== undefined);

        return result.join("");
    }

    return numbers.join("");
}
